import { useCallback, useState } from "react";
import { secondsToTime } from "utils/numbers";
import { getTimeLeft } from "utils/timer";
import { getCardsWithDetails, toCardsWithDetails } from "./cardDetails";
import {
  CART_CONFIG,
  INITIAL_CART_CONTEXT_STATE,
  SOURCE_METHODS,
  SAVINGS_OPERATIONS
} from "./constants";
import {
  addBulkCards,
  emptyCart as emptyCartRequest,
  getAffiliateUrl as getAffiliateUrlRequest,
  getBrand,
  getCards,
  mountCart,
  removeCard as removeCardRequest,
  validateServiceResponse
} from "./requests";
import { useCartValidations } from "./useCartValidations";
import { useShowSnackbar } from "hooks/useShowSnackbar";
import { getBrandUuidWithMaxValue } from "./brandWithMaxValue";
import { INTERNAL_PATHS } from "routes/paths";

export const useCartCards = () => {
  const [cart, setCart] = useState(INITIAL_CART_CONTEXT_STATE.cart);
  const { preValidateCart } = useCartValidations();
  const { addNotification } = useShowSnackbar();

  const handleError = useCallback(
    ({ err, sourceMethod }) => {
      setCart((currentValues) => ({
        ...currentValues,
        isCartLoading: false,
        errorMessage: err
      }));

      if (sourceMethod === SOURCE_METHODS.ADD_CARDS) return;

      addNotification({ message: err, variant: "error", persist: true });

      // TODO add window.ra() log
    },
    [addNotification]
  );

  const getInitialCards = useCallback(
    async ({ userID }) => {
      setCart((currentValues) => ({ ...currentValues, isCartLoading: true }));

      try {
        const result = await getCards({ uuid: userID });

        const timeLeft = getTimeLeft(result.reserved_until, result.server_time);

        setCart((currentValues) => ({
          ...currentValues,
          cards: toCardsWithDetails(result.items),
          cartUuid: result.basket_uuid,
          secondsLeft: timeLeft,
          timeLeft: secondsToTime(timeLeft),
          totalSavings: result.total_savings,
          isCartLoading: false,
          total: result.total,
          isInitialCardsLoaded: true
        }));
      } catch (error) {
        handleError({ err: error, sourceMethod: SOURCE_METHODS.GET_CARDS });
      }
    },
    [handleError]
  );

  const emptyCart = useCallback(
    async ({ userID }) => {
      try {
        await emptyCartRequest({ uuid: userID });
        setCart((currentValues) => ({ ...currentValues, cards: [] }));
      } catch (err) {
        handleError({ err, sourceMethod: SOURCE_METHODS.EMPTY_CART });
      } finally {
        setCart((currentValues) => ({ ...currentValues, timeIsUp: true }));
      }
    },
    [handleError]
  );

  const addMultipleCards = async ({
    cards: cardsToAdd,
    groupedListingEnabled,
    slug,
    value,
    quantity,
    discount,
    userID,
    productSourceId
  }) => {
    setCart((currentValues) => ({ ...currentValues, isCartLoading: true }));
    try {
      let result;

      if (groupedListingEnabled) {
        result = await mountCart({
          product_source_id: productSourceId,
          quantity,
          user_uuid: userID,
          discount,
          value
        });
      } else {
        if (
          CART_CONFIG.ENABLE_PRE_VALIDATION &&
          !preValidateCart({ addedCards: cardsToAdd, cartCards: cart.cards })
        )
          return;
        const cardsWithDetails = cardsToAdd.map((card) => {
          const { card_details } = card;
          const details = getCardsWithDetails({
            card_details
          });
          return {
            ...card,
            card_details: details
          };
        });

        result = await addBulkCards({ cards: cardsWithDetails });
      }
      // TODO: check if this validation will be necessary in the future
      await validateServiceResponse(result);
      const timeLeft = getTimeLeft(result.reserved_until, result.server_time);
      const timeIsUp = timeLeft <= 0;
      setCart((currentValues) => ({
        ...currentValues,
        timeIsUp,
        cards: toCardsWithDetails(result.items, slug),
        isCartLoading: false,
        cartUuid: result.basket_uuid,
        secondsLeft: timeLeft,
        timeLeft: secondsToTime(timeLeft),
        totalSavings: SAVINGS_OPERATIONS.ADD(
          currentValues.totalSavings,
          result.total_savings
        ),
        total: result.total
      }));
    } catch (err) {
      const error = err.response.data.error;
      if (error === "Unauthorized") {
        window.location.assign(INTERNAL_PATHS.USER_LOG_IN);
        return;
      }

      handleError({ err: error, sourceMethod: SOURCE_METHODS.ADD_CARDS });
    }
  };

  const removeCard = async ({
    brandUUID: brand_uuid,
    listingUUID: listing_uuid,
    userID: user_uuid
  }) => {
    setCart((currentValues) => ({ ...currentValues, isCartLoading: true }));
    try {
      const result = await removeCardRequest({
        brand_uuid,
        listing_uuid,
        user_uuid
      });

      setCart((currentValues) => ({
        ...currentValues,
        cards: toCardsWithDetails(result.items),
        cartUuid: result.basket_uuid,
        isCartLoading: false,
        totalSavings: result.total_savings,
        total: result.total
      }));
      addNotification({
        message: "The item has been removed",
        variant: "success",
        persist: true
      });
    } catch (error) {
      setCart((currentValues) => ({ ...currentValues, isCartLoading: false }));
      handleError({ err: error, sourceMethod: SOURCE_METHODS.REMOVE_CARD });
    }
  };

  const setAffiliateUrl = useCallback(async () => {
    const maxValueBrandUuid = getBrandUuidWithMaxValue(cart.cards);

    try {
      const brand = await getBrand(maxValueBrandUuid);

      if (!brand) {
        setCart((currentValues) => ({
          ...currentValues,
          affiliateUrl: ""
        }));
      }

      try {
        const result = await getAffiliateUrlRequest({ slug: brand.slug });

        setCart((currentValues) => ({
          ...currentValues,
          affiliateUrl: result || ""
        }));
      } catch ({ message }) {
        setCart((currentValues) => ({
          ...currentValues,
          affiliateUrl: ""
        }));
      }
    } catch (error) {
      setCart((currentValues) => ({
        ...currentValues,
        affiliateUrl: ""
      }));
    }
  }, [cart.cards]);

  const clearErrorMessage = () => {
    setCart((currentValue) => ({ ...currentValue, errorMessage: null }));
  };

  return {
    cart,
    addMultipleCards,
    removeCard,
    setAffiliateUrl,
    getInitialCards,
    emptyCart,
    clearErrorMessage
  };
};
